// src/lib/trajectoryLayouts.ts
// 軌跡マップのレイアウト戦略（版非依存化・2026-07-09、相談役依頼・CEO承認）
//
// TrajectoryMap.tsxは「点を打って線でつなぐ」描画だけを担当し、
// 「各カードの言葉をどの座標に置くか」はこのファイルのlayoutStrategyが決める。
//
// 2026-07-26・CEO方針決定：ロジカル版専用の意味座標＋軸表示（logicalLayoutStrategy）は廃止し、
// 全カード種別をmakeCategoryLayoutStrategy（稜線ティア方式）に統一した。理由：稜線ティア
// （難易度＝カード種別順位）とロジカル版固有の軸（動的-静的／広げる-深める）は表現している
// 意味が別物で、TrajectoryMap内に2つの文法が混在するとユーザーの理解コストが上がるため、
// 「軌跡マップ＝難易度を視覚化する場所」という単一の理解に統一する。
// なお実座標データ（LOGICAL_PLOT）自体は失われない——`gridPlot.ts`がMindGarden側の211語グリッドの
// 土台として引き続き使っている。TrajectoryMapから軸表示を外すのは「捨てる」のではなく
// 「表示場所を分ける」という位置づけ。
//
// それ以外の版は、カードの意味とは無関係な決定論的スパイラル配置を使う
// （トレース体験(a)のみ。版ごとの意味空間(b)は今回スコープ外）。

import { classifyWord } from "@/constants/wordOpenClose";
import type { RecordEntry } from "@/types/Entry";
import type { CardType } from "@/types/Plan";
import { getWordPlot } from "@/lib/mindGarden/wordPlot";

export type LayoutPoint = {
  x: number; // -10〜+10（PLOT_JPと同じ値域に揃える）
  y: number; // -10〜+10
  suit?: string; // 色分けに使う。無ければ呼び出し側のcolor propにフォールバック
  text: string;
};

/** 太さを持つ滑らかな接続線の1点。ノイズ適用済み・多点補間済み */
export type CurvePoint = {
  x: number;
  y: number;
  radius: number; // 相対的な太さ
  t: number; // 0〜1。アニメーション進行度との対応用
};

export type LayoutResult = {
  points: LayoutPoint[];
  /** true のとき、軸線・軸ラベル（広げる/深める/静的/動的）を表示する */
  hasSemanticAxes: boolean;
  /** 太さ・ゆらぎを持つ滑らかな接続線（無ければ従来通りpoints間を直線で結ぶ・後方互換） */
  curvePoints?: CurvePoint[];
};

export type LayoutStrategy = (entries: RecordEntry[]) => LayoutResult;

// スパイラル配置の定数（見た目の調整はここだけで完結する）
const SPIRAL_ANGLE_STEP = (Math.PI * 2) / 6; // 1点ごとに60度回転
const SPIRAL_R_MIN = 2;
const SPIRAL_R_STEP = 1.1;
const SPIRAL_R_MAX = 9.3; // -10〜10の範囲内に収める（軸ラベル分の余白を残す）

/**
 * logical以外の版：カードの意味とは無関係に、引いた順番だけを使って
 * 決定論的なスパイラル状に点を配置する（カードごとの座標データ設計は不要）。
 */
export const spiralLayoutStrategy: LayoutStrategy = (entries) => {
  const filtered = entries.filter((e) => e.source !== "topic");

  const points: LayoutPoint[] = filtered.map((entry, i) => {
    const angle = i * SPIRAL_ANGLE_STEP;
    const radius = Math.min(SPIRAL_R_MAX, SPIRAL_R_MIN + i * SPIRAL_R_STEP);
    return {
      x: radius * Math.cos(angle),
      y: radius * Math.sin(angle),
      text: entry.card,
    };
  });

  return { points, hasSemanticAxes: false };
};

// ============================================================
// 山の稜線レイアウト（2026-07-11・Kazuto発案／同日CEO指示で意味づけを決定Cベースに差し替え／
// 同日Kazuto指摘で度合い（グラデーション）化／2026-07-13・累積方式＋発散収束/受け入れ度合いの
// 2軸に再設計）
//
// 【2026-07-13再設計】前後軸の意味を精緻化（詳細はwordOpenClose.ts参照）：
//   前＝「受け入れ度合い」＝前にどんな内容が来ても自然に乗れるか（文脈の有無ではなく柔軟性）
//   後＝「発散/収束」＝話を複数方向に開いたまま渡せるか、1点にまとめてしまうか
// この2軸を「1つ前のカードとの区間ごとの遷移」として累積的に高さへ変換する：
//   方向（上り/下り）＝ボトルネック方式（2026-07-13・Kazuto承認）。前のbackと今のfrontの
//     min（小さい方）が0.5以上なら下り、それ未満なら上り。「両方とも開いているときだけ安心して
//     下れる。前が閉じていても今のカードが受け入れにくくても、どちらか一方が閉じていれば上りに
//     引っ張られる」というボトルネックの発想。以前は前のback単体で判定していたため、back閉じ語
//     （つまり・結局等7語のみ）を引かない限りずっと下り続ける偏りがあった（実機確認で発覚）。
//     front軸の閉じ語（しかし・ところが・したがって等・約29語）も上りのきっかけになるため、
//     山らしい起伏が出やすくなる。
//   急さ（friction）＝前のbackと今のfrontの平均的な噛み合わなさ。両方開いていれば摩擦ゼロ
//     （平坦・安全）、どちらかでも閉じていれば摩擦大（急・危うい）。急な下りも起こりうる
//     （Kazuto指摘：急降下も見ていて危うい＝急さは方向と独立した「見ていてハラハラする度合い」
//     であるべき）。ユーザー向けの説明は「難しければ勾配がキツくなり、簡単なら横ばいに近くなる」
//     の一文に収め、方向の判定ロジック自体はユーザーには見せない。
// 以前は各カードの高さをx軸からの絶対値として独立計算していたが、これだと見た目の坂の急さが
// 隣接カードとの相対差分になり、意図した「その区間の急さ」と一致しないケースがあった
// （Kazuto・CTO間の設計議論で発見・2026-07-13）。累積方式に変更し、区間ごとのfrictionが
// そのまま見た目の坂の急さになるようにした。1枚目は前が無いため高さ0（基準線）から開始。
// 正方形キャンバスで急さが誇張されて見える件・角度しきい値の粗さの2点は今回のスコープ外（既知の宿題）。
// ============================================================

const RIDGE_ANGLE_MAX = 45; // 度（frictionが最大＝1のときの上限角度）
const RIDGE_ANGLE_JITTER = 4; // 度（同じfriction同士でも高さに差を出すための微調整幅）
const RIDGE_STEP_RADIUS = 3; // 1区間あたりの最大の高さ変化量（friction=1・角度45度のとき）。
// 全体の値域は-10〜+10（PLOT_JPに揃える）だが累積方式のためこの値は区間単位。クランプで
// 範囲外にはみ出さないようにする。初回チューニング値・実機確認後に調整可。

// 語ごとに決定論的な疑似ランダム値を作る（同じ語なら毎回同じ角度になる。同じ開き度合いの語同士の
// ばらつき＝RIDGE_ANGLE_JITTERに使う）
function hashWord(word: string): number {
  let h = 0;
  for (let i = 0; i < word.length; i++) {
    h = (h * 31 + word.charCodeAt(i)) >>> 0;
  }
  return h;
}

/**
 * logical以外の版：山の稜線レイアウト（累積方式）。
 * xは引いた順に応じて-10〜+10に均等配置（回答数が多い/少ないどちらでも収まるよう可変幅。
 * 横長キャンバスや横スクロールへの対応は今回未着手・後日検討）。
 * yは「1つ前のカードのback（発散/収束）」と「今のカードのfront（受け入れ度合い）」の
 * 組み合わせから区間ごとのfrictionを算出し、前の点からの積み上げで高さを決める。
 * 日の出演出（保存時アニメ）は今回スコープ外。
 */
export const makeRidgeLayoutStrategy = (_cardType: CardType): LayoutStrategy => (entries) => {
  const filtered = entries.filter((e) => e.source !== "topic");
  const n = filtered.length;

  let y = 0;
  const points: LayoutPoint[] = filtered.map((entry, i) => {
    const x = n > 1 ? -10 + (i / (n - 1)) * 20 : 0;

    if (i > 0) {
      const prev = classifyWord(filtered[i - 1].card);
      const curr = classifyWord(entry.card);
      const sign = Math.min(prev.back, curr.front) >= 0.5 ? -1 : 1; // ボトルネック：どちらかでも閉じていれば上り
      const friction = 1 - (prev.back + curr.front) / 2; // 0=噛み合って平坦〜1=噛み合わず急

      const h = hashWord(entry.card);
      const jitter = ((h % 1000) / 1000 - 0.5) * RIDGE_ANGLE_JITTER * 2;
      const angleDeg = Math.max(0, Math.min(RIDGE_ANGLE_MAX, friction * RIDGE_ANGLE_MAX + jitter));
      const delta = sign * RIDGE_STEP_RADIUS * Math.sin((angleDeg * Math.PI) / 180);

      y = Math.max(-10, Math.min(10, y + delta));
    }

    return { x, y, text: entry.card };
  });

  return { points, hasSemanticAxes: false };
};

// ============================================================
// カテゴリ・レイアウト（2026-07-20・Kazuto指示／2026-07-21・Kazuto指摘で簡素化）
//
// 稜線レイアウトの課題「線の抑揚のなさ」を、MindGarden用に構築した8カテゴリ・全6デッキ対応の
// 通し表（src/lib/mindGarden/wordCategories.ts）を使って解決する。
//
// 【2026-07-21改修】初回実装はgenerateBranch（mode:"micro"）による有機的な骨格生成＋太さ・ノイズ
// 付きcurvePointsだった。デプロイ後の実機確認でKazutoより指摘：
//   ①結節点間の線が不要に何度も折れ曲がる（有機的すぎて不自然）
//   ②線の太さで進行順序を表すのは視覚的に良くない
//   ③右側の結節点が綺麗な階段状に並ぶのは、branch生成ロジックの副作用であり意図的な配置ではなく
//     信頼性に疑問（実際、branchのx/yをmin/max正規化していたため、生成過程の癖がそのまま座標の
//     並びに出てしまっていた）
// これを受け、「結節点の位置の表現に通し表を活かすだけ」のシンプルな設計に変更。
//   x：稜線レイアウトと同じ、引いた順に応じて-10〜+10へ均等配置（＝左から右へ進行順）
//   y：getWordPlot()で語の意味座標（ロジカル版の実座標、無ければカテゴリ重心）をそのまま使う
// curvePointsは返さない（TrajectoryMap側の直線・一定太さのフォールバック描画に委ねる）。
// generateBranch／branchGeneratorはMindGarden（マクロ・複数セッション集約）用として温存し、
// このレイアウトからは切り離した。
// 意味座標を借用しているが、x軸の意味が「静的/動的」ではなく「引いた順」であるため、
// logicalと混同させないよう hasSemanticAxes は false のまま。
// ============================================================

// ============================================================
// 種別ごとの固定高さティア（2026-07-26・Kazuto発案／同日CTO室で実装）
//
// 「難しければ勾配がキツくなる」だけでなく、「稜線そのものの高さがカード種別（お題タイプ）で
// 固定される」という設計。-10〜+10の値域を5等分し、各種別の中心値とする。
// freeは5段階のランクに含まれないため、暫定でスタンダードと同じ帯（中央）にフォールバック
// （2026-07-26・Kazuto確認済み）。
//
// 既存の「単語カテゴリによる起伏」は無くさず、種別の帯の中に圧縮して残す
// （TIER_BAND_HALF_WIDTHの幅に収める）。1セッション＝1カード種別のため、稜線らしい上下動は
// 帯の中で保たれたまま、種別が変わればマップ全体の高さゾーンが変わる形になる。
//
// 【2026-07-26追記・圧縮】初回実装（中心 ±8〜±4・帯幅±2）はデプロイ後の実機確認でKazutoより
// 「種別間の高低差が出すぎている」と指摘。上限（ロジカル）を下げ、下限（エモーション）を上げ、
// 他3種は均等に収まる形へ、中心の間隔・帯幅とも半分に圧縮（±8→±4・帯幅±2→±1）。
//
// 【2026-07-26追記・順位入れ替え】難しい順に上から：①ロジカル②スタンダード③ライフ④ストーリー
// テリング⑤エモーション、にKazuto判断で確定（当初はライフ2位・スタンダード3位だったが入れ替え）。
// 理由：ライフは接続詞の多寡だけでなく「人生」がテーマのため、つい現実の経験に基づいた話をしよう
// として一語ずつ深く考え込みやすく、ストーリーテリングより難しいという体感評価。
//
// 【2026-07-26追記・正規化基準の修正（CEO承認・バグ修正扱い）】圧縮式が前提にしていた±10
// （getWordPlotの理論上の値域）は、非ロジカル語の大半が8カテゴリの代表座標（重心）に
// フォールバックするため実際にはほぼ到達しない。CATEGORY_CENTROIDSの実測値（ロジカル版実座標が
// あり重心を算出できる6カテゴリ）はy=-0.875（逆接／時間伸縮）〜+5.25（揺らぎ・余白）の範囲に
// 収まり、想定していた範囲の3割程度しか使えていなかった。これが「稜線がほぼ平坦に見える」という
// CEO/Kazuto指摘（7/26）の根本原因。帯幅・ティア間隔（＝種別間の分離）は変えず、正規化の基準を
// 実測レンジに直す。誤った前提を正しい前提に直すバグ修正であり、演出の強さを恣意的に変える調整
// ではない。
// ============================================================

const DIFFICULTY_TIER_CENTER: Record<CardType, number> = {
  logical: 4,
  standard: 2,
  life: 0,
  storytelling: -2,
  emotion: -4,
  free: 2, // スタンダードと同じ帯にフォールバック（順位入れ替えでスタンダードの中心値が2になったため追従）
};

const TIER_BAND_HALF_WIDTH = 1; // 種別帯の高さ幅（中心から±1。2026-07-26圧縮：±2→±1）

// 2026-07-26追記：TrajectoryMap.tsx側のY軸表示レンジ用に、稜線ティアが実際に取りうる
// 絶対値の最大（＝ティア中心の最大絶対値＋帯幅）を余白付きでエクスポートする。
// 従来TrajectoryMap.tsx側は理論値±10を前提に描画していたが、稜線ティアの実際の可動域は
// ±5（ロジカル4±1〜エモーション-4±1）に過ぎず、Kazuto実機確認で「上下に余白が多すぎる」
// と指摘（SIZE_H拡大の効果が薄まっていた）。この値を直接インポートさせることで、
// 今後ティア中心・帯幅を再調整した際も表示側が自動追従し、値のズレを防ぐ。
const CATEGORY_LAYOUT_Y_MARGIN = 1; // 点が枠の端ぎりぎりに張り付かないための余白
export const CATEGORY_LAYOUT_Y_HALF_RANGE =
  Math.max(...Object.values(DIFFICULTY_TIER_CENTER).map((v) => Math.abs(v))) +
  TIER_BAND_HALF_WIDTH +
  CATEGORY_LAYOUT_Y_MARGIN;

// getWordPlotが返すy値のうち、非ロジカル語がフォールバックするカテゴリ代表座標（重心）の
// 実測レンジ。CATEGORY_CENTROIDS（src/lib/mindGarden/categoryCentroids.ts）から算出した
// 実際の最小・最大値（2026-07-26確認）。ロジカル語の実座標（LOGICAL_PLOTの±10）がそのまま
// 使われるケースはこの範囲を超えることがあるため、正規化後に±1へクランプして吸収する。
const OBSERVED_CATEGORY_Y_MIN = -0.875; // 実測：逆接／時間伸縮
const OBSERVED_CATEGORY_Y_MAX = 5.25; // 実測：揺らぎ・余白
const OBSERVED_CATEGORY_Y_RANGE = OBSERVED_CATEGORY_Y_MAX - OBSERVED_CATEGORY_Y_MIN;

/**
 * 全カードタイプ対応：カテゴリベースのレイアウト。
 * x＝引いた順（稜線レイアウトと同じ）。
 * y＝カード種別ごとの固定ティア中心に、通し表で解決した意味座標（無ければ0）を
 *   実測レンジ（OBSERVED_CATEGORY_Y_MIN〜MAX）基準で-1〜+1に正規化し、
 *   ±TIER_BAND_HALF_WIDTHへスケールして加算（＝種別で高さゾーンが変わり、その中で起伏が残る）。
 * curvePointsは返さない＝TrajectoryMap側は直線・一定太さで結ぶ（従来の稜線レイアウトと同じ見た目）。
 */
export const makeCategoryLayoutStrategy = (cardType: CardType): LayoutStrategy => (entries) => {
  const filtered = entries.filter((e) => e.source !== "topic");
  const n = filtered.length;
  if (n === 0) return { points: [], hasSemanticAxes: false };

  const tierCenter = DIFFICULTY_TIER_CENTER[cardType] ?? 0;

  const points: LayoutPoint[] = filtered.map((entry, i) => {
    const x = n > 1 ? -10 + (i / (n - 1)) * 20 : 0;
    const plot = getWordPlot(entry.card);
    const rawY = plot ? Math.max(-10, Math.min(10, plot.y)) : 0;
    const normalizedY =
      OBSERVED_CATEGORY_Y_RANGE > 0
        ? Math.max(
            -1,
            Math.min(1, (2 * (rawY - OBSERVED_CATEGORY_Y_MIN)) / OBSERVED_CATEGORY_Y_RANGE - 1)
          )
        : 0;
    const y = Math.max(-10, Math.min(10, tierCenter + normalizedY * TIER_BAND_HALF_WIDTH));
    return { x, y, text: entry.card };
  });

  return { points, hasSemanticAxes: false };
};
